import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { ERequestType, IExpandedMetric, IMetric } from '@static/models/metrics';
import { AuthService } from '@static/services/auth.service';
import { HttpService } from '@static/services/http.service';
import { SnackService } from '@static/services/snack.service';
import { mean, min, max, std } from 'mathjs';
import moment from 'moment';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { DecimalPipe } from '@angular/common';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FillHeightDirective } from '@static/directives/fill-height.directive';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StorageService } from '@static/services/storage.service';
import { KEY_METRICS_DATES } from '@static/storage-keys';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatSelectModule } from '@angular/material/select';

interface IRouteAverage {
  route: string;
  average: number;
  records: number;
  min: number;
  max: number;
  sd: number;
}

@Component({
  selector: 'app-metrics',
  imports: [
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    FillHeightDirective,
    DecimalPipe,
  ],
  templateUrl: './metrics.component.html',
  styleUrl: './metrics.component.scss',
  providers: [
    provideMomentDateAdapter({
      parse: {
        dateInput: 'DD/MM/YYYY',
      },
      display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMM YYYY'
      }
    }, { useUtc: true }),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
  ]
})
export class MetricsComponent implements AfterViewInit {
  private _router: Router = inject(Router);
  private _httpService: HttpService = inject(HttpService);
  private _authService: AuthService = inject(AuthService);
  private _snackService: SnackService = inject(SnackService);
  private _storageService: StorageService = inject(StorageService);

  @ViewChild(MatSort) sort!: MatSort;

  private _metrics: IExpandedMetric[];

  public daterangeStartForm: FormControl;
  public daterangeEndForm: FormControl;

  public routeAverage: MatTableDataSource<IRouteAverage, MatPaginator>;
  public routaAverageColumns: string[];

  public versions: string[];
  public selectedVersion?: string;
  public requestTypes: ERequestType[];
  public selectedRequestType?: ERequestType;

  constructor() {
    this._metrics = [];
    this.versions = [];
    this.requestTypes = [];

    this.routeAverage = new MatTableDataSource();
    this.routaAverageColumns = ['route', 'average', 'records', 'min', 'max', 'sd'];
    this.daterangeStartForm = new FormControl('', []);
    this.daterangeEndForm = new FormControl('', []);

    if (!this._authService.isGlobalAdmin) {
      this._router.navigate(['/']);
      return;
    };

    // Check for saved dates
    const saved = this._storageService.loadData(KEY_METRICS_DATES);
    if (saved) {
      const dates = JSON.parse(saved) as [number, number];
      const end = moment.utc(dates[1]);
      const start = moment.utc(dates[0]);
      this.daterangeStartForm.setValue(start);
      this.daterangeEndForm.setValue(end);
    } else {
      this.resetDates();
    }

    this._getMetrics();
  }

  /**
   * Link sort to table
   */
  ngAfterViewInit(): void {
    this.routeAverage.sort = this.sort;
  }

  /**
   * Get the list of metrics based on the current filter parameters
   */
  private _getMetrics(): void {
    const data = {
      start: this.daterangeStartForm.value ? moment.utc(this.daterangeStartForm.value).valueOf() : undefined,
      end: this.daterangeEndForm.value ? moment.utc(this.daterangeEndForm.value).valueOf() : undefined,
    };
    this._httpService.postP<IMetric[]>(`${environment.backendUri}/admin/metrics`, data)
      .then((res: IMetric[]) => {
        // Ignore scraping routes
        const ignoreSet = ['php', '.env', '.git', 'HEAD/'];
        const projectUploadId = /^p?\d+(?:-\d+)*$/;
        const projectUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(_[0-9]+)?$/;
        const converted: IExpandedMetric[] = res.filter((r) => !ignoreSet.some(i => r.route.includes(i))) as IExpandedMetric[];

        converted.forEach((metric: IExpandedMetric) => {
          // Convert all time to the start of the day
          metric.date = Math.trunc(metric.date / 86400000.0) * 86400000;
          // Merge project routes
          const splitRoute = metric.route.split('/');
          metric.type = ERequestType[splitRoute[0] as keyof typeof ERequestType];
          if (splitRoute[1].match(/^\d+.\d+.\d+$/)) {
            metric.version = splitRoute[1];
            splitRoute[1] = "<VER>";
          }

          if (metric.route.includes('/project/')) {
            splitRoute.forEach((seg: string, idx: number) => {
              if (projectUploadId.test(seg) || projectUUID.test(seg) || !isNaN(+seg)) splitRoute[idx] = '<ID>';
            });
          }
          metric.route = splitRoute.join('/');

        });

        this.versions = Array.from(new Set(converted.map(o => o.version))) as string[];
        this.requestTypes = Array.from(new Set(converted.map(o => o.type))) as ERequestType[];

        this._metrics = converted;
        this._calculateRouteAverages(this._metrics);
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) this._router.navigate(['/']);
        else this._snackService.openErrorSnack('Failed to get application metrics', err);
      });
  }

  /**
   * Update the shown data
   */
  public update(): void {
    const data = this._metrics.filter((m: IExpandedMetric) => {
      if (this.selectedVersion === undefined && this.selectedRequestType === undefined) return true;
      return ((!!this.selectedVersion && m.version === this.selectedVersion) || !this.selectedVersion) &&
        ((!!this.selectedRequestType && m.type === this.selectedRequestType) || !this.selectedRequestType);
    });
    this._calculateRouteAverages(data);
  }

  /**
   * Calculate the average execution time for the various routes
   */
  private _calculateRouteAverages(input: IExpandedMetric[]): void {
    const rAve = input.reduce((acc: Record<string, number[]>, cur: IMetric) => {
      if (cur.route in acc) acc[cur.route].push(cur.exe_time);
      else acc[cur.route] = [cur.exe_time];
      return acc;
    }, {} as Record<string, number[]>);
    const data = [] as IRouteAverage[];
    Object.keys(rAve).forEach((key: string) => data.push({
      route: key,
      average: mean(rAve[key]),
      records: rAve[key].length,
      min: min(rAve[key]),
      max: max(rAve[key]),
      sd: std(rAve[key]).valueOf() as number
    }));
    this.routeAverage.data = data;
  }

  /**
   * Reset the date range to the current data date plus the project update frequency
   */
  public resetDates(): void {
    const end = moment.utc().set({ hours: 23, minute: 59, second: 59, millisecond: 999});
    let start = end.clone().set({ hours: 0, minute: 0, second: 0, millisecond: 0});
    start = start.utc().subtract(1, 'd');

    this.daterangeStartForm.setValue(start);
    this.daterangeEndForm.setValue(end);

    this.daterangeUpdate();
  }

  /**
   * Update the displayed assigned work based on the date range
   */
  public daterangeUpdate(): void {
    const start = this.daterangeStartForm.value ? moment.utc(this.daterangeStartForm.value) : undefined;
    const end = this.daterangeEndForm.value
      ? moment.utc(this.daterangeEndForm.value).set({ hours: 23, minute: 59, second: 59, millisecond: 999})
      : undefined;

    this._storageService.saveData(KEY_METRICS_DATES, JSON.stringify([start, end]));
    this._getMetrics();
  }
}
